/**
 * Canonical service-level domain vocabulary shared across the monorepo.
 *
 * "Service level" is the customer-facing SLA tier (its display strings are the
 * {@link ServiceLevel} members). "Platform SLA id" is the stable machine id stored on an
 * address as `platformSlaId` (the {@link DefaultSla} members). This module is the single
 * source of truth for both, their id->display-name and id->minutes maps, the security-only
 * constants, the legacy-value canonicaliser, and the display-name resolvers.
 *
 * This package is framework-agnostic: no React, MUI, Apollo, or DB access.
 */
import dayjs from 'dayjs';
/** Customer-facing service-level display names. String values are stable and stored as data. */
export var ServiceLevel;
(function (ServiceLevel) {
    ServiceLevel["Inactive"] = "Inactive";
    ServiceLevel["NonMember"] = "Non-Member";
    ServiceLevel["WarrantyEssentials"] = "Warranty Essentials";
    ServiceLevel["Essentials"] = "Essentials";
    ServiceLevel["CPPEssentialsPlus"] = "CPP Essentials Plus";
    ServiceLevel["EssentialsPlus"] = "Essentials Plus";
    ServiceLevel["CPPPriority"] = "CPP Priority";
    ServiceLevel["Priority"] = "Priority";
    ServiceLevel["CPPProactive"] = "CPP Proactive";
    ServiceLevel["Proactive"] = "Proactive";
    ServiceLevel["CPPSignature"] = "CPP Signature";
    ServiceLevel["Signature"] = "Signature";
    ServiceLevel["SecurityOnly"] = "Secure";
    ServiceLevel["OvInternal"] = "OV - Internal";
    ServiceLevel["Legacy"] = "Legacy";
})(ServiceLevel || (ServiceLevel = {}));
/** Stable platform SLA ids stored on an address as `platformSlaId`. */
export var DefaultSla;
(function (DefaultSla) {
    DefaultSla["Essentials"] = "essentials";
    DefaultSla["EssentialsPlus"] = "essentials_plus";
    DefaultSla["Priority"] = "priority";
    DefaultSla["SecurityOnly"] = "security_only";
    DefaultSla["Proactive"] = "proactive";
    DefaultSla["Signature"] = "signature";
})(DefaultSla || (DefaultSla = {}));
/**
 * Maps a platform SLA id (e.g. `essentials_plus`) to its service-level display name *without*
 * the "SLA" suffix (e.g. "Signature", not "Signature SLA"). Note `security_only` maps to
 * `Secure`, not "Security Only".
 */
export const PLATFORM_SLA_DISPLAY_NAMES = {
    [DefaultSla.SecurityOnly]: ServiceLevel.SecurityOnly,
    [DefaultSla.Essentials]: ServiceLevel.Essentials,
    [DefaultSla.EssentialsPlus]: ServiceLevel.EssentialsPlus,
    [DefaultSla.Priority]: ServiceLevel.Priority,
    [DefaultSla.Proactive]: ServiceLevel.Proactive,
    [DefaultSla.Signature]: ServiceLevel.Signature,
};
/** Maps a platform SLA id to its numeric SLA value in minutes. */
export const PLATFORM_SLA_TO_SLA_VALUE = {
    [DefaultSla.Essentials]: 60,
    [DefaultSla.EssentialsPlus]: 60,
    [DefaultSla.Priority]: 30,
    [DefaultSla.Proactive]: 30,
    [DefaultSla.Signature]: 10,
    [DefaultSla.SecurityOnly]: 30,
};
/**
 * Legacy display-name-keyed SLA minutes map. Keyed by {@link ServiceLevel} display name rather than
 * platform SLA id (the id-keyed equivalent is {@link PLATFORM_SLA_TO_SLA_VALUE}). `NonMember` and
 * `WarrantyEssentials` have no SLA value (null).
 */
export const DEFAULT_SLA = {
    [ServiceLevel.CPPSignature]: 10,
    [ServiceLevel.Signature]: 10,
    [ServiceLevel.OvInternal]: 30,
    [ServiceLevel.Legacy]: 30,
    [ServiceLevel.Inactive]: 30,
    [ServiceLevel.CPPProactive]: 30,
    [ServiceLevel.Proactive]: 30,
    [ServiceLevel.CPPPriority]: 30,
    [ServiceLevel.Priority]: 30,
    [ServiceLevel.SecurityOnly]: 30,
    [ServiceLevel.CPPEssentialsPlus]: 60,
    [ServiceLevel.EssentialsPlus]: 60,
    [ServiceLevel.Essentials]: 60,
    [ServiceLevel.NonMember]: null,
    [ServiceLevel.WarrantyEssentials]: null,
};
/** Client-selected membership states (chosen during activation), distinct from resolved SLA tiers. */
export const ClientSelectedServiceLevels = {
    Undecided: 'Undecided',
    DeclinedMembership: 'Declined Membership',
};
/** Display label for a pre-LitS site with no active SLA. */
export const PRE_LITS_PLACEHOLDER_SERVICE_LEVEL = 'Pre-LitS Priority';
/**
 * Sentinel `platformSlaId` for a pre-LitS site with no active SLA. Not a real platform SLA —
 * callers map it to {@link PRE_LITS_PLACEHOLDER_SERVICE_LEVEL}.
 */
export const PRE_LITS_PLACEHOLDER_PLATFORM_SLA_ID = 'pre_lits_priority';
/** The base sellable membership tiers (excludes security-only, CPP variants, and non-member states). */
export const BaseServiceLevels = {
    EssentialsPlus: ServiceLevel.EssentialsPlus,
    Priority: ServiceLevel.Priority,
    Proactive: ServiceLevel.Proactive,
    Signature: ServiceLevel.Signature,
};
const RECOGNIZED_DISPLAY_NAMES = new Set([
    ...Object.values(PLATFORM_SLA_DISPLAY_NAMES),
    ServiceLevel.NonMember,
]);
const SERVICE_LEVEL_VALUES = new Set(Object.values(ServiceLevel));
// DB tech debt: some places store `Essentials+`, others `Essentials Plus`.
export function formatMembershipName(name, addCppPrefix = false) {
    const formattedName = name.replace('+', ' Plus').trim();
    return addCppPrefix ? `CPP ${formattedName}` : formattedName;
}
export function resolveActivatedMembershipName(platformSlaId, addCppPrefix = false) {
    const slaName = platformSlaId ? PLATFORM_SLA_DISPLAY_NAMES[platformSlaId] : undefined;
    if (!slaName) {
        return ServiceLevel.NonMember;
    }
    const cppName = `CPP ${slaName}`;
    return addCppPrefix && SERVICE_LEVEL_VALUES.has(cppName) ? cppName : slaName;
}
/** True when a service-level display name is a CPP (complimentary period) variant. */
export function isCPPServiceLevel(value) {
    return !!value?.includes('CPP');
}
/**
 * True while an address's complimentary period (CPP) is active: `cppExpDate` falls on a future
 * calendar day (local time; a date expiring today is no longer active). CPP membership is defined
 * by this field alone — an address can be in CPP without any subscription — so "is in CPP" checks
 * must use this instead of subscription status.
 */
export function isCppActive(cppExpDate) {
    return !!cppExpDate && dayjs(cppExpDate).isAfter(dayjs(), 'day');
}
export function isSecurityOnlySla({ platformSlaId, }) {
    return platformSlaId === DefaultSla.SecurityOnly;
}
export function canonicalizeLegacyServiceLevel(value) {
    if (!value)
        return undefined;
    if (RECOGNIZED_DISPLAY_NAMES.has(value)) {
        return value;
    }
    if (value === 'CPP Essentials Plus' || value === 'Warranty Essentials') {
        return PLATFORM_SLA_DISPLAY_NAMES.essentials_plus;
    }
    if (value === 'CPP Priority') {
        return PLATFORM_SLA_DISPLAY_NAMES.priority;
    }
    if (value === 'CPP Proactive') {
        return PLATFORM_SLA_DISPLAY_NAMES.proactive;
    }
    if (value === 'CPP Signature') {
        return PLATFORM_SLA_DISPLAY_NAMES.signature;
    }
    return undefined;
}
/** Resolves a platform SLA id to its numeric SLA value in minutes; null for missing/unknown ids. */
export function getSlaFromPlatformSlaId(platformSlaId) {
    if (!platformSlaId)
        return null;
    return PLATFORM_SLA_TO_SLA_VALUE[platformSlaId] ?? null;
}
/**
 * Resolves a partner-facing service level name from a platform SLA id. A null/unknown SLA
 * resolves to Non-Member.
 *
 * When the address's active subscription is trialing, the legacy value contract surfaced the
 * service level with a "CPP " prefix (e.g. "CPP Signature"). Pass `isTrialing: true` to re-add
 * the prefix. The prefix is only applied when a real CPP variant exists in {@link ServiceLevel};
 * a trialing Secure SLA stays "Secure" since there is no "CPP Secure" value.
 */
export function getServiceLevelName(platformSlaId, opts) {
    if (!platformSlaId) {
        return ServiceLevel.NonMember;
    }
    const serviceLevel = PLATFORM_SLA_DISPLAY_NAMES[platformSlaId];
    if (!serviceLevel) {
        return ServiceLevel.NonMember;
    }
    if (opts?.isTrialing) {
        const trialingName = formatMembershipName(serviceLevel, true);
        if (SERVICE_LEVEL_VALUES.has(trialingName)) {
            return trialingName;
        }
    }
    return serviceLevel;
}
/**
 * Policy/address seam: resolves an address's `platformSlaId` to its canonical service-level display
 * name. A null `platformSlaId` means the address has no active SLA and resolves to Non-Member; the
 * `platform_sla_id` foreign key guarantees any non-null id is a known tier.
 */
export function getEffectiveServiceLevelForAddress({ platformSlaId, }) {
    if (!platformSlaId) {
        return ServiceLevel.NonMember;
    }
    return PLATFORM_SLA_DISPLAY_NAMES[platformSlaId] ?? ServiceLevel.NonMember;
}
/**
 * Maps a persisted `platformSlaId` (e.g. `essentials_plus`) to its human-readable service level
 * name. `undefined` in stays `undefined` (e.g. an absent change record); a cleared SLA (`null`/empty)
 * surfaces as Non-Member; an unknown id falls back to the raw value. Unlike
 * {@link getEffectiveServiceLevelForAddress}, this preserves an `undefined` input and echoes unknown ids.
 */
export function platformSlaIdToServiceLevelName(value) {
    if (value === undefined)
        return undefined;
    if (!value)
        return ServiceLevel.NonMember;
    return PLATFORM_SLA_DISPLAY_NAMES[value] ?? value;
}
const SERVICE_LEVEL_NAME_TO_PLATFORM_SLA_ID = Object.fromEntries(Object.entries(PLATFORM_SLA_DISPLAY_NAMES).map(([platformSlaId, name]) => [
    name,
    platformSlaId,
]));
/**
 * Reverse of {@link PLATFORM_SLA_DISPLAY_NAMES}: maps a service level display name (with or without
 * a leading "CPP " prefix, since platformSlaId is CPP-agnostic) back to its platformSlaId.
 * Non-Member / unknown names resolve to `null`.
 */
export function serviceLevelNameToPlatformSlaId(name) {
    if (!name)
        return null;
    const base = name.replace(/^CPP /, '').trim();
    return SERVICE_LEVEL_NAME_TO_PLATFORM_SLA_ID[base] ?? null;
}
